// Client-side distance + walk-time estimates. Uses the same walking speed the
// backend planner falls back to (78 m/min) so the numbers stay consistent.
const WALK_MPM = 78.0;
const METERS_PER_MILE = 1609.34;

export function haversineMeters(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const p1 = (aLat * Math.PI) / 180;
  const p2 = (bLat * Math.PI) / 180;
  const dphi = ((bLat - aLat) * Math.PI) / 180;
  const dl = ((bLng - aLng) * Math.PI) / 180;
  const h =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Returns { walkMin, miles } from an [lat,lng] origin to a place with lat/lng.
export function estimateTo(origin, place) {
  if (!origin || place?.lat == null || place?.lng == null) return null;
  const m = haversineMeters(origin[0], origin[1], place.lat, place.lng);
  return { walkMin: Math.max(1, Math.round(m / WALK_MPM)), miles: m / METERS_PER_MILE };
}

export function fmtMiles(miles) {
  if (miles == null) return "";
  return miles < 0.1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;
}

// A friendly "does this fit my break?" hint (round trip + a little time on site).
export function gapFit(walkMin) {
  if (walkMin == null) return null;
  const rt = walkMin * 2 + 10;
  if (rt <= 30) return { label: "Fits a 30-min gap", tone: "good" };
  if (rt <= 45) return { label: "Fits a 45-min gap", tone: "good" };
  if (rt <= 60) return { label: "Fits a 1-hr gap", tone: "mod" };
  return { label: "Best for longer breaks", tone: "busy" };
}

// Attach { _est } to each item and sort nearest-first (items without coords go last).
export function withDistance(list, origin) {
  return (list || [])
    .map((it) => ({ ...it, _est: estimateTo(origin, it) }))
    .sort((a, b) => (a._est?.walkMin ?? 1e9) - (b._est?.walkMin ?? 1e9));
}
